<template>
    <div class="innovation-trends-risk-card">
        <AonContainer class="h-100">
            <AonRow class="mt-xl h-100">
                <AonCol class="grid-cols-12 p-0 flex-column justify-center h-100">
                    <AonCard
                        :title="`${entityStore.entity.name}'s Litigation Risk Gaps`"
                        :desc="`Products and technologies which have no moat protection at ${entityStore.entity.name} have the following litigation risks`"
                        class="h-100"
                    >
                        <div class="card-content" style="height: 500px">
                            <AonCoverLoading
                                :loading="allLoading"
                                title="Loading Trend Risk..."
                                message="Products and Technology Areas within Moat2Product will need to be identified."
                            ></AonCoverLoading>
                            <MoatTable
                                v-if="axisRange.length > 0 && !noRiskTrends && !allLoading"
                                class="w-100 h-100"
                                :key="`tech_areas_${tableUpdater}`"
                                :class="themeClass"
                                :column-defs="colDefs"
                                :tooltip-interaction="true"
                                :tooltip-mouse-track="true"
                                :tooltip-show-delay="500"
                                tooltip-show-mode="whenTruncated"
                                :autoGroupColumnDef="autoGroupColumnDef"
                                :groupRemoveLowestSingleChildren="true"
                                :groupDisplayType="groupDisplayType"
                                :treeData="true"
                                :getDataPath="getDataPath"
                                :row-data="finalTableData"
                                :autoSizeStrategy="sizingStrategy"
                                @grid-ready="gridInit"
                                @cell-clicked="onCellClicked"
                            />
                            <AonCoverLoading
                                v-if="noRiskTrends"
                                :loading="true"
                                :show-spinner="false"
                                :title="`No Risk Trends Found`"
                            >
                                <template #header>
                                    <div class="icon">
                                        <font-awesome-icon
                                            icon="fas fa-circle-exclamation"
                                            class="text-stone-800"
                                            size="2xl"
                                        />
                                    </div>
                                </template>
                            </AonCoverLoading>
                        </div>
                    </AonCard>
                </AonCol>
            </AonRow>
        </AonContainer>
    </div>
</template>

<script setup>
import { ref, inject, onBeforeMount, nextTick, computed, onMounted } from 'vue'
import { useEntityStore, usePatentViewerStore, useMoat2ProductStore } from '@/stores'
import { getLitigationRiskGaps } from '@/api/risk.js'

import { useI18n } from 'vue-i18n'

import { useFlag } from '@unleash/proxy-client-vue'
const allowDrillIn = useFlag('ipAlph.drillIn')
const isProUser = useFlag('ipAlpha.proUsers')

// Moat Table specific
import { AgGridVue as MoatTable } from '@ag-grid-community/vue3'
import { get } from '@okta/okta-auth-js'

// injectors
const filters = inject('filters')
const logger = inject('logger')
const { t } = useI18n()

const entityStore = useEntityStore()
const patentViewerStore = usePatentViewerStore()
const moat2ProductStore = useMoat2ProductStore()

const loading = ref(true)
const tableData = ref([])
const finalTableData = ref([])
const noRiskTrends = ref(false)
const gridApi = ref(null)
const tableUpdater = ref(0)
const getDataPath = ref(null)
const axisRange = ref([0, 10])
const sizingStrategy = ref({
    type: 'fitProvidedWidth',
    width: 2000,
})
const colDefs = ref([
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('risk.tooltips.riskScore'),
            headerName: 'Risk Score',
        },
        field: 'riskScore',
        headerTooltip: 'Risk Score',
        flex: 1,
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('risk.tooltips.caseCountbyYear'),
            headerName: 'Case Count By Year',
        },
        field: 'caseCountHistory',
        headerTooltip: 'Case Count By Year',
        width: 220,
        cellRenderer: 'agSparklineCellRenderer',
        cellRendererParams: {
            sparklineOptions: {
                type: 'column',
                xKey: 'year',
                yKey: 'caseCount',
                fill: '#2A79D2',
                valueAxisDomain: axisRange.value,
                axis: { type: 'number' },
                tooltip: {
                    renderer: (params) => {
                        const { x, y } = params.datum
                        return {
                            title: `${x}:`,
                            content: y,
                        }
                    },
                },
            },
        },
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('risk.tooltips.caseCount'),
            headerName: 'Case Count',
        },
        field: 'caseCount',
        headerTooltip: 'Case Count',
        width: 150,
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.caseCount.toLocaleString()
            }
        },
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('risk.tooltips.avgCaseLength'),
            headerName: 'Average Case Length',
        },
        field: 'avgCaseLength',
        headerTooltip: 'Average Case Length',
        width: 220,
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.avgCaseLength.toLocaleString()
            }
        },
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('risk.tooltips.patentsAsserted'),
            headerName: 'Patents Asserted',
        },
        field: 'patentCount',
        headerTooltip: 'Patents Asserted',
        flex: 1,
        cellRenderer: 'MTCustomCellLinkWContext',
        cellRendererParams: {
            allowClick: isProUser.value,
            cellClick: async (params) => {
                patentViewerStore.nodeIdList.push(params.data.nodeId)
                patentViewerStore.technologyName = params.data.displayName
                if (entityStore.entity) {
                    patentViewerStore.entityPkList.push(entityStore.entity.aon_entity_pk)
                    patentViewerStore.entityName = entityStore.entity.name
                }
                patentViewerStore.listViewType = 'lit'
                await nextTick()
                patentViewerStore.showPatentViewerList = true
            },
        },
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.patentCount.toLocaleString()
            }
        },
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('risk.tooltips.knownDamages'),
            headerName: 'Known Damages',
        },
        field: 'totalDamages',
        headerTooltip: 'Known Damages',
        flex: 1,
        valueFormatter: (p) => {
            if (p.data) {
                return filters.abbreviate(p.data.totalDamages)
            }
        },
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('risk.tooltips.highestKnownDamages'),
            headerName: 'Highest Known Damages',
        },
        field: 'maxIndividualDamages',
        headerTooltip: 'Highest Known Damages',
        width: 270,
        valueFormatter: (p) => {
            if (p.data) {
                return filters.abbreviate(p.data.maxIndividualDamages)
            }
        },
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('risk.tooltips.npeCases'),
            headerName: 'NPE Cases',
        },
        field: 'npeCaseCount',
        headerTooltip: 'NPE Cases',
        flex: 1,
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.npeCaseCount.toLocaleString()
            }
        },
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('risk.tooltips.npeEntities'),
            headerName: 'NPE Entities',
        },
        field: 'npeEntityCount',
        headerTooltip: 'NPE Entities',
        flex: 1,
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.npeEntityCount.toLocaleString()
            }
        },
    },
    {
        suppressHeaderMenuButton: true,
        cellRenderer: 'MTContextMenu',
        cellRendererParams: {
            actionsList: [
                {
                    name: 'View Details',
                    icon: 'fa-boxes-stacked',
                    actionName: 'details',
                    detailsAction: (params) => {
                        entityStore.drillInTargetId = null
                        setTimeout(() => {
                            entityStore.drillInTargetId = params.data.nodeId
                            entityStore.showDrillIn = true
                        }, 100)
                    },
                },
                isProUser.value
                    ? {
                          name: 'View Patents',
                          icon: 'fa-square-list',
                          actionName: 'patents',
                          patentsAction: async (params) => {
                              patentViewerStore.nodeIdList.push(params.data.nodeId)
                              patentViewerStore.technologyName = params.data.displayName
                              if (entityStore.entity) {
                                  patentViewerStore.entityName = entityStore.entity.name
                              }
                              patentViewerStore.listViewType = 'lit'
                              await nextTick()
                              patentViewerStore.showPatentViewerList = true
                          },
                      }
                    : null,
            ],
            highlightLabel: true,
        },
        pinned: 'right',
        width: 60,
    },
])
const autoGroupColumnDef = ref(null)
const groupDisplayType = ref(null)
const themeClass = ref('ag-theme-quartz')

onBeforeMount(() => {
    defineTableGroups()
    getDataPath.value = (data) => {
        return data.nodeHierarchy.map((item) => item)
    }
    groupDisplayType.value = 'singleColumn'
})

onMounted(() => {
    onGetLitRiskGaps()
})

const allLoading = computed(() => {
    return loading.value || moat2ProductStore.productAlignmentLoading
})

const gridInit = (params) => {
    gridApi.value = params.api
    params.api.hideOverlay()
}

const onGetLitRiskGaps = async () => {
    loading.value = true
    try {
        const { data } = await getLitigationRiskGaps(moat2ProductStore.alignmentPk)
        if (data.length === 0) {
            noRiskTrends.value = true
            loading.value = false
            return
        }

        finalTableData.value = data
    } catch (error) {
        logger.error(error)
    } finally {
        loading.value = false
    }
}

const onCellClicked = async (params) => {
    if (
        !allowDrillIn.value ||
        params.data.nodeType === 'company' ||
        params.column.colId !== 'ag-Grid-AutoColumn'
    ) {
        return
    }
    entityStore.drillInTargetId = params.data.nodeId
    await nextTick()
    entityStore.showDrillIn = true
}

const defineTableGroups = () => {
    autoGroupColumnDef.value = {
        minWidth: 400,
        pinned: 'left',
        field: 'displayName',
        tooltipField: 'displayName',
        suppressMovable: true,
        lockPosition: 'left',
        headerName: 'Product',
        cellRendererParams: {
            suppressCount: true,
        },
        cellClass: (params) => {
            if (params.data) {
                let allowConditions =
                    allowDrillIn.value &&
                    params.value !== entityStore.entity?.name &&
                    params.data.nodeType !== 'company'

                return allowConditions ? 'cell-link' : ''
            }
        },
    }
}
</script>
<style lang="scss" scoped>
.innovation-trends-risk-card {
    width: 100%;
    position: relative;
}
</style>
